import {Dispatch, SetStateAction, useState} from 'react';

import {downloadBlob} from '~/modules/utils';
import {WeeklyChallengeList} from '~/types';
import {useWeeklyChallengeEntrants} from './useWeeklyChallengeEntrants';
import {
  hasDuplicateQuestionIds,
  newAnswerRow,
  RequiredAnswerRow,
} from './weeklyChallengeEntrants';

export const WeeklyChallengeEntrantsSearch = ({
  challengeList,
}: {
  challengeList: WeeklyChallengeList;
}) => {
  const {isSearching, errorMsg, result, search, reset} =
    useWeeklyChallengeEntrants();

  const [challengeId, setChallengeId] = useState('');
  const [weekId, setWeekId] = useState('');
  const [weekEndDate, setWeekEndDate] = useState('');
  const [rows, setRows] = useState<RequiredAnswerRow[]>(() => [newAnswerRow()]);

  // Any edit makes the previous result, and any search in flight, stale
  const edit =
    <T,>(setter: Dispatch<SetStateAction<T>>) =>
    (value: SetStateAction<T>) => {
      setter(value);
      reset();
    };

  const updateRow = (id: string, change: Partial<RequiredAnswerRow>) =>
    edit(setRows)(prev =>
      prev.map(row => (row.id === id ? {...row, ...change} : row)),
    );

  const hasDuplicateQuestion = hasDuplicateQuestionIds(rows);

  const canSearch =
    !!challengeId &&
    !!weekId.trim() &&
    !!weekEndDate &&
    !hasDuplicateQuestion &&
    rows.every(
      ({questionId, answerId}) => questionId.trim() && answerId.trim(),
    );

  const onSearch = () =>
    search({
      challengeId,
      weekId: weekId.trim(),
      weekEndDate,
      requiredAnswers: rows.map(({questionId, answerId}) => ({
        questionId: questionId.trim(),
        answerId: answerId.trim(),
      })),
    });

  return (
    <div className="card bg-base-100 shadow my-5">
      <div className="card-body gap-3">
        <h2 className="card-title">Find weekly draw entrants</h2>
        <p className="text-sm">
          Players who completed the week by the end of the date below (UK time)
          and gave every answer listed.
        </p>

        <label className="form-control w-[32.5rem] max-w-full">
          <span className="label-text font-semibold">Challenge</span>
          <select
            className="select select-bordered"
            value={challengeId}
            onChange={e => edit(setChallengeId)(e.target.value)}>
            <option value="" disabled>
              Select a challenge
            </option>
            {challengeList.map(challenge => (
              <option key={challenge.challengeId} value={challenge.challengeId}>
                {challenge.title} ({challenge.challengeId})
              </option>
            ))}
          </select>
        </label>

        <label className="form-control w-[32.5rem] max-w-full">
          <span className="label-text font-semibold">Week ID</span>
          <input
            type="text"
            className="input input-bordered"
            placeholder="e.g. sleepovers-sorted-week"
            value={weekId}
            onChange={e => edit(setWeekId)(e.target.value)}
          />
        </label>

        <label className="form-control w-[32.5rem] max-w-full">
          <span className="label-text font-semibold">Week end date</span>
          <input
            type="date"
            className="input input-bordered"
            value={weekEndDate}
            onChange={e => edit(setWeekEndDate)(e.target.value)}
          />
          <span className="label-text-alt pt-1">
            The whole of this day counts, up to 00:00 UK time the next day.
          </span>
        </label>

        <div>
          <span className="label-text font-semibold">Required answers</span>
          {rows.map(row => (
            <div key={row.id} className="flex gap-2 pt-2 items-center">
              <input
                type="text"
                className="input input-bordered w-64"
                placeholder="Question ID, e.g. q-12"
                value={row.questionId}
                onChange={e => updateRow(row.id, {questionId: e.target.value})}
              />
              <input
                type="text"
                className="input input-bordered w-64"
                placeholder="Answer ID, e.g. a-01"
                value={row.answerId}
                onChange={e => updateRow(row.id, {answerId: e.target.value})}
              />
              <button
                className="btn btn-sm btn-ghost"
                disabled={rows.length === 1}
                onClick={() =>
                  edit(setRows)(prev => prev.filter(r => r.id !== row.id))
                }>
                Remove
              </button>
            </div>
          ))}
          {hasDuplicateQuestion && (
            <p className="text-red-600 text-sm pt-2">
              Each question can only be listed once. A player can't give two
              different answers to the same question.
            </p>
          )}
          <button
            className="btn btn-sm btn-outline mt-2"
            onClick={() => edit(setRows)(prev => [...prev, newAnswerRow()])}>
            Add another question
          </button>
        </div>

        <div className="flex gap-3 items-center">
          <button
            className="btn btn-primary"
            disabled={!canSearch || isSearching}
            onClick={onSearch}>
            {isSearching ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              'Search'
            )}
          </button>
          {errorMsg && <p className="text-red-600 text-sm">{errorMsg}</p>}
        </div>

        {result?.kind === 'none' && (
          <p className="font-semibold">{result.message}</p>
        )}
        {result?.kind === 'entrants' && (
          <div className="flex gap-3 items-center flex-wrap">
            <p className="font-semibold">
              {result.userCount}{' '}
              {result.userCount === 1 ? 'user matches' : 'users match'}
            </p>
            <button
              className="btn btn-sm btn-primary"
              onClick={() =>
                downloadBlob(
                  result.csv,
                  result.fileName,
                  'text/csv;charset=utf-8;',
                )
              }>
              Download CSV
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
